import { useEffect, useRef } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Consent storage v2 (AEPD-compliant): granular categories.
// Estructura: { v: '2', necessary: true, analytics: bool, marketing: bool, date: ISO, version: string }
// 'necessary' siempre true (cookies tecnicas). El resto requieren opt-in explicito.
export const POLICY_VERSION = '2026-04-26-v1';

interface ConsentRecord {
  v: string;
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  date: string;
  version: string;
}

export const getConsent = (): ConsentRecord | null => {
  try {
    const raw = localStorage.getItem('funcomunion_consent');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Migrate legacy format ({ accepted: true, date: ... }) -> reset, requires re-consent
    if (parsed?.v !== '2') return null;
    return parsed as ConsentRecord;
  } catch { return null; }
};
export const hasConsent = () => !!getConsent();
// Save granular consent. categories may include analytics, marketing.
export const saveConsentDetailed = (opts: { analytics?: boolean; marketing?: boolean }) => {
  const rec: ConsentRecord = {
    v: '2',
    necessary: true,
    analytics: !!opts.analytics,
    marketing: !!opts.marketing,
    date: new Date().toISOString(),
    version: POLICY_VERSION,
  };
  localStorage.setItem('funcomunion_consent', JSON.stringify(rec));
};
// Backwards-compat alias used in AuthModal/CookieBanner that previously called saveConsent()
// to mark "ha aceptado al menos lo basico". Ahora marca solo necesarias.
export const saveConsent = () => saveConsentDetailed({});
export const clearConsent = () => localStorage.removeItem('funcomunion_consent');

interface LegalModalProps {
  tipo: 'privacidad' | 'terminos' | 'cookies';
  onClose: () => void;
}

const TITLE_KEYS: Record<string, string> = {
  privacidad: 'home.legalTitlePrivacy',
  terminos: 'home.legalTitleTerms',
  cookies: 'home.legalTitleCookies',
};

const CONTENT: Record<string, React.ReactNode> = {
  privacidad: (
    <div className="space-y-6 text-sm text-[#374151] leading-relaxed">
      <p className="text-xs text-[#6B7280]">Última actualización: enero 2025</p>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">1. Responsable del tratamiento</h3>
        <p>Funcomunion (en adelante, «la empresa»), con dirección de contacto en <strong>pedidos@funcomunion.es</strong>, es el responsable del tratamiento de los datos personales que usted nos facilita a través de este sitio web.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">2. Datos que recogemos</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Nombre y apellidos</li>
          <li>Dirección de correo electrónico</li>
          <li>Dirección de envío (cuando se complete el pedido)</li>
          <li>Fotografías de referencia que usted cargue voluntariamente</li>
          <li>Datos de configuración del pedido (tipo de figura, personalizaciones, accesorios)</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">3. Finalidad del tratamiento</h3>
        <p>Los datos se utilizan exclusivamente para:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Gestionar y ejecutar el pedido de figura personalizada</li>
          <li>Comunicarnos con usted durante el proceso de fabricación</li>
          <li>Gestionar el envío y la entrega del producto</li>
          <li>Generar previsualizaciones mediante inteligencia artificial a partir de las fotografías facilitadas</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">4. Base legal</h3>
        <p>El tratamiento se ampara en:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>La ejecución del contrato de compraventa (art. 6.1.b RGPD).</li>
          <li>El consentimiento expreso del usuario para las fotografías (art. 6.1.a RGPD).</li>
          <li>El interés legítimo del responsable para fines de seguridad y prevención de fraude (art. 6.1.f RGPD): registro de datos de conexión (IP, navegador y momento) en eventos sensibles —intentos de inicio de sesión, registro de cuenta, inicio del configurador y creación de pedidos— para detectar accesos no autorizados, registros automatizados o uso abusivo del servicio.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">5. Cesión de datos</h3>
        <p>No cedemos sus datos a terceros salvo obligación legal. Las imágenes de previsualización son procesadas por la API de OpenAI sujeta a su propia política de privacidad; no se usan para entrenar modelos. La dirección IP se enriquece con datos de geolocalización a través del servicio <strong>ipapi.co</strong> (Cloudflare, encargado del tratamiento) únicamente para fines de seguridad descritos en la sección 4.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">6. Conservación</h3>
        <p>Los datos contractuales se conservan durante la vigencia de la relación y posteriormente durante los plazos legalmente exigibles (hasta 5 años para documentación mercantil). Los datos de conexión recogidos por seguridad (IP y eventos de autenticación / configurador / pedidos) se conservan <strong>30 días</strong> y se eliminan automáticamente.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">7. Sus derechos</h3>
        <p>Puede ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a <strong>pedidos@funcomunion.es</strong>. También desde la sección "Tus derechos RGPD" en su panel de usuario puede:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Descargar todos sus datos</strong> en formato JSON portable.</li>
          <li><strong>Eliminar su cuenta y datos personales</strong>. Los pedidos pagados se anonimizarán (obligación fiscal de conservación 6 años, Ley General Tributaria art. 66) y las fotos de referencia se borrarán inmediatamente.</li>
        </ul>
        <p className="mt-2">También puede presentar reclamación ante la Agencia Española de Protección de Datos (<a href="https://aepd.es" target="_blank" rel="noopener" className="underline text-[#7A5F0A]">aepd.es</a>).</p>
      </section>

      <section className="border-t pt-6 mt-6">
        <h3 className="font-semibold text-[#1E3A5F] mb-2">8. Datos de menores de edad</h3>
        <p>Cuando el pedido es para una figura conmemorativa de la Primera Comunión (típicamente menor de 14 años), se aplica la siguiente normativa adicional (RGPD art. 8 y LOPDGDD art. 7). Para tramitar el pedido se debe completar <strong>una</strong> de las dos vías legales siguientes:</p>
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li><strong>Vía A — Consentimiento del titular de la patria potestad/tutela</strong>: si quien realiza el pedido es la madre, el padre o tutor/a legal del menor, debe marcar dos casillas separadas:
            <ul className="list-disc pl-5 mt-1 text-xs">
              <li>Declaración de tener la patria potestad o tutela del menor.</li>
              <li>Autorización expresa al tratamiento de los datos del menor (nombre, fecha de comunión y, en su caso, fotografía de referencia).</li>
            </ul>
          </li>
          <li><strong>Vía B — Aportación de fotografías no identificables</strong>: si quien realiza el pedido <em>no es</em> el titular de la patria potestad (p. ej. abuelo/a, padrino/madrina, amigo/a haciendo un regalo) puede usar el servicio sin consentimiento parental siempre que <strong>declare expresamente</strong> que las fotografías que aporte (si las hay) <strong>no muestran la cara ni rasgos identificables del menor</strong> (uniforme, accesorios, paisaje, etc., sin persona identificable). En este supuesto las fotos no constituyen dato personal del menor en los términos del RGPD art. 4.1, y sirven exclusivamente como inspiración estética para la figura. Si la persona aporta una fotografía identificable, deberá usar la Vía A.</li>
          <li><strong>Finalidad única</strong>: los datos aportados se usan EXCLUSIVAMENTE para fabricar la figura del pedido. No se ceden a terceros distintos del proveedor de IA y la imprenta artesanal.</li>
          <li><strong>Eliminación automática a los 30 días</strong>: las fotografías de referencia se borran de nuestros servidores 30 días después de la entrega del pedido (o tras la cancelación / abandono del wizard) mediante un proceso programado y se notifica al cliente y al administrador por correo separado.</li>
          <li><strong>Auditoría del consentimiento</strong>: cada vía aceptada (A o B) se registra con fecha, IP, navegador, versión exacta de esta política y texto exacto aceptado, conforme al art. 7.1 RGPD.</li>
          <li><strong>Datos no usados para entrenar modelos de IA</strong>: la API de OpenAI utilizada para las previsualizaciones tiene contractualmente prohibido usar los datos enviados para entrenar sus modelos.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">9. Tratamiento mediante Inteligencia Artificial</h3>
        <p>El sitio utiliza la API de OpenAI (gpt-image-1) para generar previsualizaciones de la figura a partir de la configuración y, opcionalmente, de la fotografía de referencia del menor.</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Encargado del tratamiento</strong>: OpenAI Inc. (EE.UU.) actúa como encargado en virtud de su Data Processing Addendum (<a href="https://openai.com/policies/data-processing-addendum" target="_blank" rel="noopener" className="underline text-[#7A5F0A]">consultar DPA</a>).</li>
          <li><strong>Transferencia internacional</strong>: existe transferencia de datos a EE.UU. amparada en las Cláusulas Contractuales Tipo (SCC) aprobadas por la Comisión Europea.</li>
          <li><strong>Conservación en OpenAI</strong>: según su política, OpenAI conserva los datos enviados a la API durante 30 días para detección de abuso y luego los elimina. No los utiliza para entrenar modelos.</li>
          <li><strong>Decisiones automatizadas</strong>: las previsualizaciones generadas son orientativas y no constituyen decisiones automatizadas con efectos jurídicos sobre el usuario (art. 22 RGPD no aplicable).</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">10. Seguridad y medidas técnicas</h3>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Cifrado HTTPS con certificados Let's Encrypt y HSTS preload.</li>
          <li>Almacenamiento en Firebase (Google Cloud Platform, EU-WEST-1, Bélgica).</li>
          <li>Acceso al panel admin restringido por rol y autenticación de dos factores en preparación.</li>
          <li>Auditoría completa de accesos administrativos.</li>
          <li>Reglas estrictas de Firestore que impiden el acceso a datos de otros usuarios.</li>
        </ul>
      </section>
    </div>
  ),

  terminos: (
    <div className="space-y-6 text-sm text-[#374151] leading-relaxed">
      <p className="text-xs text-[#6B7280]">Última actualización: enero 2025</p>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">1. Descripción del servicio</h3>
        <p>Funcomunion ofrece figuras artesanales estilo Funko Pop personalizadas para la celebración de la Primera Comunión. Cada figura es pintada a mano por artesanos y es única.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">2. Proceso de pedido</h3>
        <ol className="list-decimal pl-5 space-y-1">
          <li>El cliente configura la figura a través del asistente online.</li>
          <li>Se genera una previsualización orientativa mediante IA (no es la pieza final).</li>
          <li>El cliente confirma el pedido y abona el importe acordado.</li>
          <li>El artesano elabora la figura y envía fotografías para aprobación.</li>
          <li>Aprobada la figura, se procede al envío.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">3. Precios y pago</h3>
        <p>Los precios incluyen IVA y están expresados en euros. Se aceptan Bizum y efectivo (contra reembolso). El pago debe completarse antes del inicio de la producción salvo acuerdo expreso.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">4. Envíos</h3>
        <p>Recogida en tienda gratuita en Calle Caballeros, 8 (Ciudad Real). Envío a domicilio: 12€ a península (Correos urgente, 1–3 días laborables). El plazo de fabricación es de 5 a 7 días laborables, o 2–4 días en modalidad urgente. Los plazos son orientativos y se cuentan desde la confirmación del pago.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">5. Devoluciones y cancelaciones</h3>
        <p>Dado el carácter personalizado del producto, no se admiten devoluciones una vez iniciada la producción, salvo defecto de fabricación. Las cancelaciones son gratuitas antes del inicio de la producción. En caso de defecto, la empresa se compromete a rehacer la figura sin coste adicional.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">6. Propiedad intelectual</h3>
        <p>Las imágenes, textos y diseños del sitio son propiedad de Funcomunion. Las fotografías aportadas por el cliente son de su exclusiva propiedad y se usan únicamente para fabricar su pedido.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">7. Legislación aplicable</h3>
        <p>Estos términos se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales del domicilio del consumidor.</p>
      </section>
    </div>
  ),

  cookies: (
    <div className="space-y-6 text-sm text-[#374151] leading-relaxed">
      <p className="text-xs text-[#6B7280]">Última actualización: enero 2025</p>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">¿Qué son las cookies?</h3>
        <p>Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Permiten al sitio recordar sus preferencias y mejorar su experiencia.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">Cookies que utilizamos</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse mt-2">
            <thead>
              <tr className="bg-[#FAF8F5]">
                <th className="text-left p-2 border border-gray-200 text-[#1E3A5F]">Nombre</th>
                <th className="text-left p-2 border border-gray-200 text-[#1E3A5F]">Tipo</th>
                <th className="text-left p-2 border border-gray-200 text-[#1E3A5F]">Finalidad</th>
                <th className="text-left p-2 border border-gray-200 text-[#1E3A5F]">Duración</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border border-gray-200">funcomunion_user</td>
                <td className="p-2 border border-gray-200">Técnica</td>
                <td className="p-2 border border-gray-200">Mantiene la sesión del usuario</td>
                <td className="p-2 border border-gray-200">Sesión</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-2 border border-gray-200">funcomunion_consent</td>
                <td className="p-2 border border-gray-200">Técnica</td>
                <td className="p-2 border border-gray-200">Registra la aceptación de esta política</td>
                <td className="p-2 border border-gray-200">1 año</td>
              </tr>
              <tr>
                <td className="p-2 border border-gray-200">Firebase Auth</td>
                <td className="p-2 border border-gray-200">Técnica</td>
                <td className="p-2 border border-gray-200">Autenticación de usuario (Google/Email)</td>
                <td className="p-2 border border-gray-200">Variable</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[#6B7280]">No utilizamos cookies publicitarias, de seguimiento o de terceros con fines comerciales.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">Gestión del consentimiento</h3>
        <p>Puede retirar su consentimiento en cualquier momento borrando las cookies de su navegador a través de la configuración de privacidad. Esto no afectará a la licitud del tratamiento previo.</p>
      </section>

      <section>
        <h3 className="font-semibold text-[#1E3A5F] mb-2">Contacto</h3>
        <p>Para cualquier consulta sobre nuestra política de cookies: <strong>pedidos@funcomunion.es</strong></p>
      </section>
    </div>
  ),
};

export function LegalModal({ tipo, onClose }: LegalModalProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0];
  const isNonSpanish = lang !== 'es';
  const titleId = `legal-modal-title-${tipo}`;
  const previousFocus = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Cierre con Escape + restaurar foco al elemento que abrió el modal (WCAG 2.4.3).
  useEffect(() => {
    previousFocus.current = (document.activeElement as HTMLElement) || null;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    // Auto-foco al modal
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handler);
      try { previousFocus.current?.focus(); } catch { /* noop */ }
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-2xl w-full max-w-2xl flex flex-col shadow-2xl outline-none"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 id={titleId} className="text-xl font-semibold text-[#1E3A5F]">{t(TITLE_KEYS[tipo])}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            aria-label={t('home.legalClose')}
          >
            <X className="w-5 h-5 text-[#6B7280]" />
          </button>
        </div>

        {/* Scrollable body — tabIndex=0 para que sea alcanzable por teclado (WCAG 2.1.1). */}
        <div className="px-6 py-5 overflow-y-auto flex-1" tabIndex={0} role="document">
          {isNonSpanish && (
            <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <p className="font-semibold mb-1">{t('home.legalDisclaimerTitle')}</p>
                <p>{t('home.legalDisclaimerBody')}</p>
              </div>
            </div>
          )}
          {CONTENT[tipo]}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-full btn-nacre font-medium text-sm"
          >
            {t('home.legalClose')}
          </button>
        </div>
      </div>
    </div>
  );
}
